import { canonicalEventType } from "./event-types";
import { PhoneError, parsePhone } from "./phone";
import { addDays, civilToday } from "./time";
import { nameParts, personName, workbookFrom, matchVenueId, foldVenue, cellText, parseGuestCount, GuestCountError } from "./workbook";

/** Client interest. Exactly one: TP top, HP high, MP medium, LP low, VLP very low, OTHER with free text. */
/** DJ: Ναι = της επιχείρησης, Όχι = όχι δικός μας (π.χ. του πελάτη), Χωρίς = καθόλου DJ στην εκδήλωση. */
export const DJ_CHOICES = ["yes", "no", "none"] as const;
export type DjChoice = (typeof DJ_CHOICES)[number];
export const DJ_LABELS: Record<DjChoice, string> = { yes: "Ναι", no: "Όχι", none: "Χωρίς" };

export const EVALUATION_CODES = ["TP", "HP", "MP", "LP", "VLP", "OTHER"] as const;
export type EvaluationCode = (typeof EVALUATION_CODES)[number];

export const VENUE_CHOICES = ["casa", "ktema", "regina", "other"] as const;
export type VenueChoice = (typeof VENUE_CHOICES)[number];

export const SERVICE_STYLES = ["buffet", "cocktail", "other"] as const;
export type ServiceStyle = (typeof SERVICE_STYLES)[number];

export const MENU_CHOICES = ["comfort", "social", "nagia", "other"] as const;
export type MenuChoice = (typeof MENU_CHOICES)[number];

export const CHURCH_CHOICES = ["on_site", "off_site"] as const;
export type ChurchChoice = (typeof CHURCH_CHOICES)[number];

export const FLOW_STAGES = ["sales", "tour", "food", "dessert", "chef"] as const;
export type FlowStage = (typeof FLOW_STAGES)[number];

export const DAY_SLOTS = ["midday", "evening"] as const;
export type DaySlot = (typeof DAY_SLOTS)[number];

export const OCCASION_CODES = ["wedding", "baptism", "wedding_and_baptism", "other", "corporate", "outdoor", "unknown"] as const;

const EVALUATION_GLOSS: Record<EvaluationCode, string> = {
  TP: "top",
  HP: "high",
  MP: "medium",
  LP: "low",
  VLP: "very low",
  OTHER: "άλλο",
};

const MENU_LABEL: Record<Exclude<MenuChoice, "other">, string> = {
  comfort: "Comfort",
  social: "Social",
  nagia: "Nagia",
};

export type InterestDate = { date: string; slot: DaySlot | null };

export type TastingFlow = Record<FlowStage, string | null>;

export type TastingDraft = {
  tasted_on: string | null;
  arrival_time: string | null;
  revisit_on: string | null;
  interest_dates: InterestDate[];
  venue_choice: VenueChoice | null;
  venue_other: string | null;
  guest_count: number | null;
  occasion: string | null;
  occasion_other: string | null;
  service_style: ServiceStyle | null;
  service_style_other: string | null;
  menu_choice: MenuChoice | null;
  menu_other: string | null;
  church: ChurchChoice | null;
  church_place: string | null;
  dj: DjChoice | null;
  orchestra: boolean | null;
  fireworks: boolean | null;
  animator: boolean | null;
  games: boolean | null;
  extras: string | null;
  given_name: string;
  family_name: string;
  phone: string | null;
  email: string | null;
  special_requests: string | null;
  table_number: string | null;
  flow: TastingFlow;
  flow_ends: TastingFlow;
  evaluation: EvaluationCode | null;
  evaluation_note: string | null;
  follow_up: string | null;
  follow_up_manual: boolean;
};

export type VenueRef = { id: string; name: string };

export type CrmSnapshot = {
  title: string;
  event_type: string | null;
  adults: number | null;
  venue_id: string | null;
  venue_name: string | null;
  start_date: string | null;
  date_label: string | null;
  notes: string | null;
  contact_name: string | null;
  given_name: string | null;
  family_name: string | null;
  phone: string | null;
  email: string | null;
  workbook: Record<string, string>;
};

export type EventChanges = {
  title?: string;
  event_type?: string | null;
  adults?: number | null;
  guests_estimated?: number;
  venue_id?: string | null;
  start_date?: string | null;
  date_precision?: "exact" | "month" | "unknown";
  notes?: string | null;
  workbook?: Record<string, string>;
};

export class TastingInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TastingInputError";
  }
}

export function visibleInterestDates(dates: InterestDate[]): InterestDate[] {
  return dates.length > 0 ? dates : [{ date: "", slot: null }];
}

/** The first row is the preferred event date. Later rows are alternatives, numbered only when there is more than one. */
export function interestDateLabel(index: number, count: number): string {
  if (index <= 0) return "Ημερομηνία εκδήλωσης";
  if (count - 1 > 1) return `Εναλλακτική ημερομηνία ${index}`;
  return "Εναλλακτική ημερομηνία";
}

export function emptyFlow(): TastingFlow {
  return { sales: null, tour: null, food: null, dessert: null, chef: null };
}

/** Minutes from start to end. Blank when either time is missing or the end is earlier. */
export function flowDurationLabel(start: string | null | undefined, end: string | null | undefined): string | null {
  const startMinutes = clockMinutes(start);
  const endMinutes = clockMinutes(end);
  if (startMinutes == null || endMinutes == null || endMinutes < startMinutes) return null;
  const minutes = endMinutes - startMinutes;
  if (minutes > 60) {
    const hours = Math.floor(minutes / 60);
    const rest = String(minutes % 60).padStart(2, "0");
    return `Διάρκεια: ${hours}ώ ${rest}'`;
  }
  return `Διάρκεια: ${minutes}'`;
}

function clockMinutes(value: string | null | undefined): number | null {
  if (!value || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) return null;
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

export function emptyDraft(): TastingDraft {
  return {
    tasted_on: null,
    arrival_time: null,
    revisit_on: null,
    interest_dates: [],
    venue_choice: null,
    venue_other: null,
    guest_count: null,
    occasion: null,
    occasion_other: null,
    service_style: null,
    service_style_other: null,
    menu_choice: null,
    menu_other: null,
    church: null,
    church_place: null,
    dj: null,
    orchestra: null,
    fireworks: null,
    animator: null,
    games: null,
    extras: null,
    given_name: "",
    family_name: "",
    phone: null,
    email: null,
    special_requests: null,
    table_number: null,
    flow: emptyFlow(),
    flow_ends: emptyFlow(),
    evaluation: null,
    evaluation_note: null,
    follow_up: null,
    follow_up_manual: false,
  };
}

export function emptySnapshot(): CrmSnapshot {
  return {
    title: "",
    event_type: null,
    adults: null,
    venue_id: null,
    venue_name: null,
    start_date: null,
    date_label: null,
    notes: null,
    contact_name: null,
    given_name: null,
    family_name: null,
    phone: null,
    email: null,
    workbook: {},
  };
}

export const FOLLOW_UP_DAYS: Partial<Record<EvaluationCode, number>> = { TP: 2, HP: 5, MP: 10, LP: 15 };

/** Days after the tasting date (or today, when the tasting has no date). VLP and OTHER stay empty. */
export function suggestFollowUp(evaluation: EvaluationCode | null, tastedOn: string | null, today: string): string | null {
  if (!evaluation) return null;
  const days = FOLLOW_UP_DAYS[evaluation];
  if (days == null) return null;
  const base = tastedOn && isIsoDate(tastedOn) ? tastedOn : today;
  return addDays(base, days);
}

export function followUpCaption(isoDate: string): string {
  const date = new Date(`${isoDate}T12:00:00Z`);
  const weekday = new Intl.DateTimeFormat("el-GR", { timeZone: "Europe/Athens", weekday: "short" }).format(date).replace(".", "").trim();
  return `Follow-up: ${weekday} ${isoDate.slice(8, 10)}/${isoDate.slice(5, 7)}`;
}

/** Recalculate while the date is still automatic. A typed or cleared date stays until the user changes it. */
export function applyFollowUp(current: TastingDraft, partial: Partial<TastingDraft>, today = civilToday("Europe/Athens")): TastingDraft {
  const next: TastingDraft = { ...current, ...partial };
  if ("follow_up" in partial) return { ...next, follow_up_manual: true };
  if (next.follow_up_manual) return next;
  if ("evaluation" in partial || "tasted_on" in partial) {
    return { ...next, follow_up: suggestFollowUp(next.evaluation, next.tasted_on, today) };
  }
  return next;
}

export function followUpFromWorkbook(book: Record<string, string>, evaluation: EvaluationCode | null, tastedOn: string | null, today = civilToday("Europe/Athens")): { follow_up: string | null; follow_up_manual: boolean } {
  const raw = (book.follow_up ?? "").trim();
  const iso = raw ? isoOrNull(cellText("follow_up", raw)) : null;
  if (book.follow_up_manual === "1") return { follow_up: iso, follow_up_manual: true };
  if (book.follow_up_manual === "0") return { follow_up: iso, follow_up_manual: false };
  if (!raw) return { follow_up: null, follow_up_manual: false };
  const suggestion = suggestFollowUp(evaluation, tastedOn, today);
  if (iso && iso === suggestion) return { follow_up: iso, follow_up_manual: false };
  return { follow_up: iso, follow_up_manual: true };
}

/** The open visit shows the workbook date, including an imported one. */
export function displayedFollowUp(draft: TastingDraft, workbook: Record<string, string>): TastingDraft {
  return { ...draft, ...followUpFromWorkbook(workbook, draft.evaluation, draft.tasted_on) };
}

/** A new sheet replaces a follow-up only after this feature stored its flag. Imported dates stay. */
export function ownedFollowUp(draft: TastingDraft, workbook: Record<string, string>): TastingDraft {
  if (workbook.follow_up_manual !== "0" && workbook.follow_up_manual !== "1") return draft;
  return displayedFollowUp(draft, workbook);
}

export function evaluationLabel(value: string | null | undefined): string {
  const parsed = parseEvaluation(value);
  if (!parsed) return (value ?? "").trim();
  return `${parsed.code} · ${EVALUATION_GLOSS[parsed.code]}`;
}

export function parseEvaluation(value: string | null | undefined): { code: EvaluationCode; note: string | null } | null {
  const raw = (value ?? "").trim();
  if (!raw) return null;
  const upper = raw.toUpperCase();
  const codes = ["VLP", "OTHER", "TP", "HP", "MP", "LP"] as const;
  for (const code of codes) {
    if (upper === code) return { code, note: null };
    if (upper.startsWith(`${code} `) || upper.startsWith(`${code}:`) || upper.startsWith(`${code}·`) || upper.startsWith(`${code}-`)) {
      const note = raw.slice(code.length).replace(/^[\s:·.-]+/, "").trim();
      return { code, note: note || null };
    }
  }
  const folded = raw.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "").replace(/\s+/g, " ");
  const legacy: Record<string, EvaluationCode> = {
    top: "TP",
    high: "HP",
    medium: "MP",
    mid: "MP",
    low: "LP",
    "very low": "VLP",
    verylow: "VLP",
    other: "OTHER",
    allo: "OTHER",
    αλλο: "OTHER",
  };
  const code = legacy[folded];
  return code ? { code, note: null } : null;
}

export function venueChoiceForName(name: string | null | undefined): { choice: VenueChoice; other: string | null } | null {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return null;
  const folded = foldVenue(trimmed);
  if (folded === "c" || folded.includes("casa")) return { choice: "casa", other: null };
  if (folded === "r" || folded.includes("regina")) return { choice: "regina", other: null };
  if (folded === "k" || folded.includes("kthma") || folded.includes("ktima") || folded.includes("kτημα")) return { choice: "ktema", other: null };
  return { choice: "other", other: trimmed };
}

export function resolveVenue(choice: VenueChoice | null, other: string | null, venues: VenueRef[]): { venue_id: string | null; label: string | null } {
  if (!choice) return { venue_id: null, label: null };
  if (choice === "other") {
    const text = (other ?? "").trim();
    const venueId = text ? matchVenueId(venues, text) : null;
    return { venue_id: venueId, label: text || "ΑΛΛΟ" };
  }
  const code = choice === "casa" ? "C" : choice === "regina" ? "R" : "K";
  const venueId = matchVenueId(venues, code);
  const name = venueId ? venues.find((venue) => venue.id === venueId)?.name ?? null : null;
  const fallback = choice === "casa" ? "CASA" : choice === "ktema" ? "ΚΤΗΜΑ" : "REGINA";
  return { venue_id: venueId, label: name ?? fallback };
}

export function parseTastingDraft(input: unknown): TastingDraft {
  if (!input || typeof input !== "object" || Array.isArray(input)) throw new TastingInputError("Η φόρμα δεν είναι έγκυρη.");
  const body = input as Record<string, unknown>;
  const evaluation = optionalEnum(body.evaluation, EVALUATION_CODES, "Η αξιολόγηση είναι μία από TP, HP, MP, LP, VLP, OTHER.");
  const draft: TastingDraft = {
    tasted_on: optionalDate(body.tasted_on, "Η ημερομηνία γευστικής δοκιμής γράφεται ως ημερομηνία."),
    arrival_time: parseArrivalTime(body.arrival_time),
    revisit_on: optionalDate(body.revisit_on, "Η επαναληπτική επίσκεψη γράφεται ως ημερομηνία."),
    interest_dates: interestDates(body.interest_dates),
    venue_choice: optionalEnum(body.venue_choice, VENUE_CHOICES, "Ο χώρος δεν είναι έγκυρος."),
    venue_other: optionalText(body.venue_other, 200),
    guest_count: optionalGuests(body.guest_count),
    occasion: optionalOccasion(body.occasion),
    occasion_other: optionalText(body.occasion_other, 200),
    service_style: optionalEnum(body.service_style, SERVICE_STYLES, "Ο τύπος εκδήλωσης δεν είναι έγκυρος."),
    service_style_other: optionalText(body.service_style_other, 200),
    menu_choice: optionalEnum(body.menu_choice, MENU_CHOICES, "Το μενού δεν είναι έγκυρο."),
    menu_other: optionalText(body.menu_other, 200),
    church: optionalEnum(body.church, CHURCH_CHOICES, "Η εκκλησία δεν είναι έγκυρη."),
    church_place: optionalText(body.church_place, 200),
    dj: optionalDj(body.dj),
    orchestra: optionalBoolean(body.orchestra, "Η ορχήστρα είναι ναι ή όχι."),
    fireworks: optionalBoolean(body.fireworks, "Τα πυροτεχνήματα είναι ναι ή όχι."),
    animator: optionalBoolean(body.animator, "Ο ανιματέρ είναι ναι ή όχι."),
    games: optionalBoolean(body.games, "Τα παιχνίδια είναι ναι ή όχι."),
    extras: optionalText(body.extras, 400),
    given_name: optionalText(body.given_name, 120) ?? "",
    family_name: optionalText(body.family_name, 120) ?? "",
    phone: optionalPhone(body.phone),
    email: optionalEmail(body.email),
    special_requests: optionalText(body.special_requests, 4000),
    table_number: parseTableNumber(body.table_number),
    flow: flowOf(body.flow),
    flow_ends: flowOf(body.flow_ends),
    evaluation,
    evaluation_note: evaluation === "OTHER" ? optionalText(body.evaluation_note, 500) : null,
    follow_up: optionalDate(body.follow_up, "Η επόμενη επικοινωνία γράφεται ως ημερομηνία."),
    follow_up_manual: body.follow_up_manual === true,
  };
  return draft;
}

export function prefillTasting(snapshot: CrmSnapshot, options?: { tastedOn?: string | null }): TastingDraft {
  const names = nameParts(snapshot.contact_name ?? "", snapshot.given_name, snapshot.family_name);
  const book = snapshot.workbook;
  const evaluation = parseEvaluation(book.evaluation);
  const venue = venueChoiceForName(snapshot.venue_name) ?? venueChoiceForName(book.wanted_venue);
  const wanted = isoOrNull(cellText("wanted_date", book.wanted_date));
  const visit = options && "tastedOn" in options ? options.tastedOn ?? null : isoOrNull(cellText("visit_date", book.visit_date || book.came));
  const menu = menuFromText(book.menu);
  const church = churchFromText(book.church);
  return {
    ...emptyDraft(),
    tasted_on: visit,
    interest_dates: wanted ? [{ date: wanted, slot: slotFromText(book.service_time) }] : [],
    venue_choice: venue?.choice ?? null,
    venue_other: venue?.choice === "other" ? venue.other : null,
    guest_count: snapshot.adults,
    occasion: allowedOccasion(snapshot.event_type),
    menu_choice: menu.choice,
    menu_other: menu.other,
    church: church.choice,
    church_place: church.place,
    dj: djChoiceFromText(book.dj),
    orchestra: djFromText(book.orchestra),
    fireworks: djFromText(book.fireworks),
    animator: djFromText(book.animator),
    games: djFromText(book.games),
    extras: blankToNull(book.extras),
    given_name: names.given_name,
    family_name: names.family_name,
    phone: blankToNull(snapshot.phone),
    email: blankToNull(snapshot.email),
    special_requests: blankToNull(book.requirements) ?? blankToNull(snapshot.notes),
    table_number: blankToNull(book.table),
    evaluation: evaluation?.code ?? null,
    evaluation_note: evaluation?.code === "OTHER" ? evaluation.note : null,
    ...followUpFromWorkbook(book, evaluation?.code ?? null, visit),
  };
}

/** Identity fields follow the live event. The sheet keeps the visit moment. */
export function presentTasting(stored: TastingDraft, snapshot: CrmSnapshot): TastingDraft {
  const names = nameParts(snapshot.contact_name ?? stored.family_name, snapshot.given_name ?? stored.given_name, snapshot.family_name ?? stored.family_name);
  const liveVenue = snapshot.venue_id ? venueChoiceForName(snapshot.venue_name) : null;
  const followed = followUpFromWorkbook(snapshot.workbook, stored.evaluation, stored.tasted_on);
  return {
    ...stored,
    given_name: names.given_name,
    family_name: names.family_name,
    phone: blankToNull(snapshot.phone),
    email: blankToNull(snapshot.email),
    guest_count: snapshot.adults,
    occasion: allowedOccasion(snapshot.event_type) ?? stored.occasion,
    venue_choice: liveVenue?.choice ?? stored.venue_choice,
    venue_other: liveVenue?.choice === "other" ? liveVenue.other : liveVenue ? null : stored.venue_other,
    follow_up: followed.follow_up,
    follow_up_manual: followed.follow_up_manual,
  };
}

export function projectTasting(input: {
  draft: TastingDraft;
  snapshot: CrmSnapshot;
  venues: VenueRef[];
  previous: TastingDraft | null;
  projectVisit: boolean;
}): { draft: TastingDraft; full_name: string; changes: EventChanges } {
  const draft = input.draft;
  const previous = input.previous;
  const snapshot = input.snapshot;
  const book: Record<string, string> = { ...snapshot.workbook };
  const changes: EventChanges = {};
  const fullName = personName({ given_name: draft.given_name, family_name: draft.family_name });
  const previousName = previous ? personName(previous) : blankToNull(snapshot.contact_name);
  if (fullName && fullName !== snapshot.title && (!snapshot.title || snapshot.title === previousName || snapshot.title === blankToNull(snapshot.contact_name) || snapshot.title.endsWith(" inquiry"))) {
    changes.title = fullName;
  }

  if (draft.occasion) {
    if (draft.occasion !== snapshot.event_type) changes.event_type = draft.occasion;
  } else if (previous?.occasion && snapshot.event_type === previous.occasion) changes.event_type = null;

  if (draft.guest_count != null) {
    if (draft.guest_count !== snapshot.adults) {
      changes.adults = draft.guest_count;
      changes.guests_estimated = 1;
    }
  } else if (previous?.guest_count != null && snapshot.adults === previous.guest_count) {
    changes.adults = null;
    changes.guests_estimated = 0;
  }

  const venue = resolveVenue(draft.venue_choice, draft.venue_other, input.venues);
  const previousVenue = previous ? resolveVenue(previous.venue_choice, previous.venue_other, input.venues) : null;
  if (draft.venue_choice) {
    if ((venue.venue_id ?? null) !== (snapshot.venue_id ?? null)) changes.venue_id = venue.venue_id;
  } else if (previous?.venue_choice && snapshot.venue_id === (previousVenue?.venue_id ?? null)) changes.venue_id = null;

  const nextRequests = draft.special_requests;
  const previousRequests = previous?.special_requests ?? null;
  if (nextRequests) {
    if (snapshot.notes !== nextRequests && (!snapshot.notes || snapshot.notes === previousRequests)) changes.notes = nextRequests;
  } else if (snapshot.notes && snapshot.notes === previousRequests) changes.notes = null;

  writeOwned(book, "wanted_venue", venue.label, previousVenue?.label ?? null);

  if (input.projectVisit) {
    writeOwned(book, "visit_date", draft.tasted_on, previous?.tasted_on ?? null);
    writeOwned(book, "came", draft.tasted_on, previous?.tasted_on ?? null);
    writeOwned(book, "table", draft.table_number, previous?.table_number ?? null);
    writeOwned(book, "evaluation", draft.evaluation, previous?.evaluation ?? null);
    writeOwned(book, "follow_up", draft.follow_up, previous?.follow_up ?? null);
    writeOwned(book, "follow_up_manual", followUpFlag(draft), followUpFlag(previous));
    writeOwned(book, "menu", menuLabel(draft), menuLabel(previous));
    writeOwned(book, "dj", djLabel(draft), djLabel(previous));
    writeOwned(book, "orchestra", yesNoLabel(draft.orchestra), yesNoLabel(previous?.orchestra));
    writeOwned(book, "fireworks", yesNoLabel(draft.fireworks), yesNoLabel(previous?.fireworks));
    writeOwned(book, "animator", yesNoLabel(draft.animator), yesNoLabel(previous?.animator));
    writeOwned(book, "games", yesNoLabel(draft.games), yesNoLabel(previous?.games));
    writeOwned(book, "extras", draft.extras, previous?.extras ?? null);
    writeOwned(book, "church", churchLabel(draft), churchLabel(previous));
    writeOwned(book, "requirements", draft.special_requests, previous?.special_requests ?? null);
    const first = draft.interest_dates[0]?.date ?? null;
    const previousFirst = previous?.interest_dates[0]?.date ?? null;
    writeOwned(book, "wanted_date", first, previousFirst);
    const agreed = isoOrNull(book.agreed_date);
    if (!agreed) {
      const wanted = isoOrNull(cellText("wanted_date", snapshot.workbook.wanted_date));
      if (first && snapshot.start_date !== first && (snapshot.start_date == null || snapshot.start_date === previousFirst || (previous == null && snapshot.start_date === wanted))) {
        changes.start_date = first;
        changes.date_precision = "exact";
      } else if (!first && previousFirst && snapshot.start_date === previousFirst) {
        changes.start_date = null;
        changes.date_precision = snapshot.date_label ? "month" : "unknown";
      }
    }
    assignWorkbook(changes, snapshot.workbook, book);
  } else if (venue.label || previousVenue?.label) {
    assignWorkbook(changes, snapshot.workbook, book);
  }

  return { draft, full_name: fullName, changes };
}

export type StoredTasting = {
  tasted_on: string | null;
  arrival_time: string | null;
  revisit_on: string | null;
  interest_dates_json: string;
  venue_choice: string | null;
  venue_other: string | null;
  guest_count: number | null;
  occasion: string | null;
  occasion_other: string | null;
  service_style: string | null;
  service_style_other: string | null;
  menu_choice: string | null;
  menu_other: string | null;
  church: string | null;
  church_place: string | null;
  dj: number | null;
  /** 1 = «Χωρίς»: no DJ at all (dj stays NULL). Added in 0018 so old 0/1 rows keep their meaning. */
  dj_none: number | null;
  orchestra: number | null;
  fireworks: number | null;
  animator: number | null;
  games: number | null;
  extras: string | null;
  special_requests: string | null;
  table_number: string | null;
  flow_json: string;
  evaluation: string | null;
  evaluation_note: string | null;
};

export function draftToStored(draft: TastingDraft): StoredTasting {
  return {
    tasted_on: draft.tasted_on,
    arrival_time: draft.arrival_time,
    revisit_on: draft.revisit_on,
    interest_dates_json: JSON.stringify(draft.interest_dates),
    venue_choice: draft.venue_choice,
    venue_other: draft.venue_other,
    guest_count: draft.guest_count,
    occasion: draft.occasion,
    occasion_other: draft.occasion_other,
    service_style: draft.service_style,
    service_style_other: draft.service_style_other,
    menu_choice: draft.menu_choice,
    menu_other: draft.menu_other,
    church: draft.church,
    church_place: draft.church_place,
    dj: draft.dj === "yes" ? 1 : draft.dj === "no" ? 0 : null,
    dj_none: draft.dj === "none" ? 1 : null,
    orchestra: yesNoStored(draft.orchestra),
    fireworks: yesNoStored(draft.fireworks),
    animator: yesNoStored(draft.animator),
    games: yesNoStored(draft.games),
    extras: draft.extras,
    special_requests: draft.special_requests,
    table_number: draft.table_number,
    flow_json: JSON.stringify({ ...draft.flow, ends: draft.flow_ends }),
    evaluation: draft.evaluation,
    evaluation_note: draft.evaluation_note,
  };
}

export function storedToDraft(row: StoredTasting): TastingDraft {
  const { flow, flow_ends: flowEnds } = flowFromStored(parseJson(row.flow_json));
  return {
    ...emptyDraft(),
    tasted_on: isoOrNull(row.tasted_on),
    arrival_time: clockOrNull(row.arrival_time),
    revisit_on: isoOrNull(row.revisit_on),
    interest_dates: interestFromJson(row.interest_dates_json),
    venue_choice: optionalEnum(row.venue_choice, VENUE_CHOICES, "") ?? null,
    venue_other: blankToNull(row.venue_other),
    guest_count: typeof row.guest_count === "number" ? row.guest_count : null,
    occasion: allowedOccasion(row.occasion),
    occasion_other: blankToNull(row.occasion_other),
    service_style: optionalEnum(row.service_style, SERVICE_STYLES, "") ?? null,
    service_style_other: blankToNull(row.service_style_other),
    menu_choice: optionalEnum(row.menu_choice, MENU_CHOICES, "") ?? null,
    menu_other: blankToNull(row.menu_other),
    church: optionalEnum(row.church, CHURCH_CHOICES, "") ?? null,
    church_place: blankToNull(row.church_place),
    dj: djFromStored(row.dj, row.dj_none),
    orchestra: yesNoFromStored(row.orchestra ?? null),
    fireworks: yesNoFromStored(row.fireworks),
    animator: yesNoFromStored(row.animator),
    games: yesNoFromStored(row.games),
    extras: blankToNull(row.extras),
    special_requests: blankToNull(row.special_requests),
    table_number: blankToNull(row.table_number),
    flow,
    flow_ends: flowEnds,
    evaluation: optionalEnum(row.evaluation, EVALUATION_CODES, "") ?? null,
    evaluation_note: row.evaluation === "OTHER" ? blankToNull(row.evaluation_note) : null,
  };
}

function interestFromJson(value: string): InterestDate[] {
  const parsed = parseJson(value);
  if (!Array.isArray(parsed)) return [];
  const dates: InterestDate[] = [];
  for (const entry of parsed) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    const date = isoOrNull(typeof record.date === "string" ? record.date : null);
    if (!date) continue;
    const slot = record.slot === "midday" || record.slot === "evening" ? record.slot : null;
    dates.push({ date, slot });
  }
  return dates;
}

function parseJson(value: string | null): unknown {
  if (!value) return null;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function assignWorkbook(changes: EventChanges, current: Record<string, string>, book: Record<string, string>) {
  const next = workbookFrom(book);
  if (JSON.stringify(next) !== JSON.stringify(workbookFrom(current))) changes.workbook = next;
}

function followUpFlag(draft: TastingDraft | null): string | null {
  if (!draft) return null;
  if (draft.follow_up_manual) return "1";
  if (draft.follow_up) return "0";
  return null;
}

function writeOwned(book: Record<string, string>, key: string, next: string | null, previous: string | null) {
  if (next) {
    book[key] = next.slice(0, 4000);
    return;
  }
  if ((book[key] ?? "") === (previous ?? "")) delete book[key];
}

function menuLabel(draft: TastingDraft | null): string | null {
  if (!draft?.menu_choice) return null;
  if (draft.menu_choice === "other") return draft.menu_other?.trim() || "Άλλο";
  return MENU_LABEL[draft.menu_choice];
}

function churchLabel(draft: TastingDraft | null): string | null {
  if (!draft?.church) return null;
  if (draft.church === "on_site") return "Εντός";
  const place = draft.church_place?.trim();
  return place ? `Εκτός · ${place}` : "Εκτός";
}

function djLabel(draft: TastingDraft | null): string | null {
  return draft?.dj ? DJ_LABELS[draft.dj] : null;
}

function djFromStored(dj: number | null, none: number | null | undefined): DjChoice | null {
  if (none === 1) return "none";
  if (dj == null) return null;
  return dj === 1 ? "yes" : "no";
}

/** Workbook text → choice. Free text such as «δικός τους» stays unrecognised (null) and is kept as written. */
export function djChoiceFromText(value: string | null | undefined): DjChoice | null {
  const folded = (value ?? "").trim().toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
  if (["χωρις", "χωρις dj", "xoris", "none", "κανενας", "-"].includes(folded)) return "none";
  const yesNo = djFromText(value);
  return yesNo == null ? null : yesNo ? "yes" : "no";
}

/** Accepts the new "yes" | "no" | "none" and the old true/false from cached clients. */
function optionalDj(value: unknown): DjChoice | null {
  if (value == null || value === "") return null;
  if (value === true) return "yes";
  if (value === false) return "no";
  if (typeof value === "string" && (DJ_CHOICES as readonly string[]).includes(value)) return value as DjChoice;
  throw new TastingInputError("Το DJ είναι ναι, όχι ή χωρίς.");
}

function yesNoLabel(value: boolean | null | undefined): string | null {
  if (value == null) return null;
  return value ? "Ναι" : "Όχι";
}

function yesNoStored(value: boolean | null): number | null {
  return value == null ? null : value ? 1 : 0;
}

function yesNoFromStored(value: number | null): boolean | null {
  return value == null ? null : value === 1;
}

function menuFromText(value: string | null | undefined): { choice: MenuChoice | null; other: string | null } {
  const text = (value ?? "").trim();
  if (!text) return { choice: null, other: null };
  const folded = text.toLowerCase();
  if (folded === "comfort") return { choice: "comfort", other: null };
  if (folded === "social") return { choice: "social", other: null };
  if (folded === "nagia") return { choice: "nagia", other: null };
  if (folded === "άλλο" || folded === "αλλο" || folded === "other") return { choice: "other", other: null };
  return { choice: "other", other: text };
}

function churchFromText(value: string | null | undefined): { choice: ChurchChoice | null; place: string | null } {
  const text = (value ?? "").trim();
  const folded = text.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
  if (folded.startsWith("εντος")) return { choice: "on_site", place: null };
  if (folded.startsWith("εκτος")) {
    const place = text.replace(/^εκτ[οό]ς\s*[·:.-]?\s*/i, "").trim();
    return { choice: "off_site", place: place || null };
  }
  return { choice: null, place: null };
}

function djFromText(value: string | null | undefined): boolean | null {
  const folded = (value ?? "").trim().toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
  if (["ναι", "yes", "1"].includes(folded)) return true;
  if (["οχι", "oxi", "no", "0"].includes(folded)) return false;
  return null;
}

function slotFromText(value: string | null | undefined): DaySlot | null {
  const folded = (value ?? "").trim().toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
  if (folded.startsWith("μεσημ")) return "midday";
  if (folded.includes("βραδ") || folded.includes("απογευ")) return "evening";
  return null;
}

function allowedOccasion(value: string | null | undefined): string | null {
  const code = canonicalEventType(value);
  return code && (OCCASION_CODES as readonly string[]).includes(code) ? code : null;
}

function optionalOccasion(value: unknown): string | null {
  if (value == null || value === "") return null;
  if (typeof value !== "string") throw new TastingInputError("Το γεγονός δεν είναι έγκυρο.");
  const code = allowedOccasion(value);
  if (!code) throw new TastingInputError("Το γεγονός δεν είναι έγκυρο.");
  return code;
}

function optionalEnum<T extends string>(value: unknown, allowed: readonly T[], message: string): T | null {
  if (value == null || value === "") return null;
  if (typeof value !== "string" || !(allowed as readonly string[]).includes(value)) {
    if (!message) return null;
    throw new TastingInputError(message);
  }
  return value as T;
}

function clockOrNull(value: string | null): string | null {
  try {
    return parseArrivalTime(value);
  } catch {
    return null;
  }
}

/** Empty is allowed. A clock time is stored as HH:MM. */
export function parseArrivalTime(value: unknown): string | null {
  if (value == null || value === "") return null;
  if (typeof value !== "string") throw new TastingInputError("Η ώρα άφιξης γράφεται ως ώρα.");
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/);
  if (!match) throw new TastingInputError("Η ώρα άφιξης γράφεται ως ώρα.");
  return `${match[1]}:${match[2]}`;
}

function optionalDate(value: unknown, message: string): string | null {
  if (value == null || value === "") return null;
  if (typeof value !== "string" || !isIsoDate(value)) throw new TastingInputError(message);
  return value;
}

const TABLE_NUMBER_MESSAGE = "Ο αριθμός τραπεζιού είναι από 1 έως 60.";

/** Empty is allowed. A whole number from 1 to 60 is kept. Other new values are rejected. */
export function parseTableNumber(value: unknown): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    if (!Number.isInteger(value) || value < 1 || value > 60) throw new TastingInputError(TABLE_NUMBER_MESSAGE);
    return String(value);
  }
  if (typeof value !== "string") throw new TastingInputError(TABLE_NUMBER_MESSAGE);
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) throw new TastingInputError(TABLE_NUMBER_MESSAGE);
  const parsed = Number(trimmed);
  if (parsed < 1 || parsed > 60) throw new TastingInputError(TABLE_NUMBER_MESSAGE);
  return String(parsed);
}

/** A number input hides values that are not digits. Those older notes stay visible as text. */
export function tableNumberUsesNumberInput(value: string | null | undefined): boolean {
  if (value == null || value.trim() === "") return true;
  return /^\d+$/.test(value.trim());
}

function optionalText(value: unknown, max: number): string | null {
  if (value == null) return null;
  if (typeof value !== "string") throw new TastingInputError("Ένα πεδίο κειμένου δεν είναι έγκυρο.");
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : null;
}

function optionalPhone(value: unknown): string | null {
  try {
    return parsePhone(value);
  } catch (error) {
    if (error instanceof PhoneError) throw new TastingInputError(error.message);
    throw error;
  }
}

function optionalEmail(value: unknown): string | null {
  const text = optionalText(value, 200);
  return text ? text.toLowerCase() : null;
}

function optionalGuests(value: unknown): number | null {
  try {
    return parseGuestCount(value, 1);
  } catch (error) {
    if (!(error instanceof GuestCountError)) throw error;
    throw new TastingInputError("Οι καλεσμένοι γράφονται ως ακέραιος αριθμός.");
  }
}

function optionalBoolean(value: unknown, message: string): boolean | null {
  if (value == null || value === "") return null;
  if (typeof value !== "boolean") throw new TastingInputError(message);
  return value;
}

function interestDates(value: unknown): InterestDate[] {
  if (value == null) return [];
  if (!Array.isArray(value)) throw new TastingInputError("Οι ημερομηνίες ενδιαφέροντος δεν είναι έγκυρες.");
  if (value.length > 8) throw new TastingInputError("Οι ημερομηνίες ενδιαφέροντος είναι έως 8.");
  const dates: InterestDate[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) throw new TastingInputError("Οι ημερομηνίες ενδιαφέροντος δεν είναι έγκυρες.");
    const record = entry as Record<string, unknown>;
    const date = optionalDate(record.date, "Η ημερομηνία ενδιαφέροντος γράφεται ως ημερομηνία.");
    const slot = optionalEnum(record.slot, DAY_SLOTS, "Η ώρα της ημερομηνίας είναι μεσημέρι ή βράδυ.");
    if (!date && !slot) continue;
    if (!date) throw new TastingInputError("Κάθε ημερομηνία ενδιαφέροντος χρειάζεται ημερομηνία.");
    dates.push({ date, slot });
  }
  return dates;
}

function flowOf(value: unknown): TastingFlow {
  const flow = emptyFlow();
  if (value == null) return flow;
  if (typeof value !== "object" || Array.isArray(value)) throw new TastingInputError("Η ροή της επίσκεψης δεν είναι έγκυρη.");
  const record = value as Record<string, unknown>;
  for (const stage of FLOW_STAGES) {
    const time = record[stage];
    if (time == null || time === "") continue;
    if (typeof time !== "string" || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) throw new TastingInputError("Η ώρα γράφεται ως ΩΩ:ΛΛ.");
    flow[stage] = time;
  }
  return flow;
}

function flowFromStored(value: unknown): { flow: TastingFlow; flow_ends: TastingFlow } {
  const flow = emptyFlow();
  const flowEnds = emptyFlow();
  if (!value || typeof value !== "object" || Array.isArray(value)) return { flow, flow_ends: flowEnds };
  const record = value as Record<string, unknown>;
  for (const stage of FLOW_STAGES) {
    const time = record[stage];
    if (typeof time === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(time)) flow[stage] = time;
  }
  const ends = record.ends;
  if (ends && typeof ends === "object" && !Array.isArray(ends)) {
    for (const stage of FLOW_STAGES) {
      const time = (ends as Record<string, unknown>)[stage];
      if (typeof time === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(time)) flowEnds[stage] = time;
    }
  }
  return { flow, flow_ends: flowEnds };
}

function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function isoOrNull(value: string | null | undefined): string | null {
  const text = (value ?? "").trim();
  return isIsoDate(text) ? text : null;
}

function blankToNull(value: string | null | undefined): string | null {
  const text = (value ?? "").trim();
  return text || null;
}
